import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchCoursePage } from "@/api/trainerAuth";
import { activateCourse, checkCourseEnrollment } from "@/api/studentAuth";
import { submitCourseReview, fetchCourseReviews } from "@/api/studentAuth";

// ============== ICONS DEFINITIONS ==============
const Icons = {
  ArrowLeft: () => <span>←</span>,
  ArrowRight: () => <span>→</span>,
  Play: () => <span>▶</span>,
  Lock: () => <span>🔒</span>,
  Star: () => <span>⭐</span>,
  Clock: () => <span>🕒</span>,
  Users: () => <span>👥</span>,
  Book: () => <span>📚</span>,
  Check: () => <span>✓</span>,
  Trophy: () => <span>🏆</span>,
  Graduation: () => <span>🎓</span>,
  Calendar: () => <span>📅</span>,
  ChevronDown: () => <span>▼</span>,
  ChevronUp: () => <span>▲</span>,
  Share: () => <span>↗</span>,
  Heart: () => <span>❤️</span>,
  Download: () => <span>⬇</span>,
  Message: () => <span>💬</span>,
  Close: () => <span>✕</span>,
  Home: () => <span>🏠</span>,
};

// ============== CSS STYLES ==============
const styles = `
    /* ============== CSS Variables ============== */
    :root {
      --primary: #6a11cb;
      --primary-light: #8a2be2;
      --primary-dark: #4a00e0;
      --secondary: #00dbde;
      --secondary-light: #00c6ff;
      --accent: #ff6b6b;
      --accent-light: #ff9e7d;
      --accent-dark: #ff4757;
      --dark: #1a1a2e;
      --dark-light: #16213e;
      --light: #f8f9fa;
      --light-gray: #e9ecef;
      --gray: #6c757d;
      --success: #00b894;
      --warning: #fdcb6e;
      --danger: #ff7675;
      --info: #00cec9;
      
      --gradient-primary: linear-gradient(135deg, var(--primary), var(--primary-light), var(--secondary));
      --gradient-secondary: linear-gradient(135deg, var(--secondary), var(--secondary-light), #36d1dc);
      --gradient-accent: linear-gradient(135deg, var(--accent), var(--accent-light), #ff9a9e);
      --gradient-dark: linear-gradient(135deg, var(--dark), var(--dark-light), #0f3460);
      
      --border-radius-xl: 25px;
      --border-radius-lg: 20px;
      --border-radius-md: 15px;
      --border-radius-sm: 10px;
      
      --box-shadow-3d: 15px 15px 30px rgba(0, 0, 0, 0.15), -5px -5px 20px rgba(255, 255, 255, 0.7);
      --box-shadow-lg: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      --box-shadow-md: 0 20px 40px rgba(0, 0, 0, 0.1);
      --box-shadow-sm: 0 10px 30px rgba(0, 0, 0, 0.08);
      --box-shadow-hover: 0 35px 60px rgba(106, 17, 203, 0.15);
      --box-shadow-neon: 0 0 20px rgba(106, 17, 203, 0.7);
      
      --transition-smooth: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
      --transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      --transition-fast: all 0.3s ease;
      
      --glass-bg: rgba(255, 255, 255, 0.12);
      --glass-border: rgba(255, 255, 255, 0.25);
      --glass-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
      
      --neon-primary: 0 0 20px rgba(106, 17, 203, 0.8);
      --neon-secondary: 0 0 20px rgba(0, 219, 222, 0.8);
      --neon-accent: 0 0 20px rgba(255, 107, 107, 0.8);
    }

    /* ============== Reset & Base ============== */
    .course-page {
      font-family: 'Cairo', 'Tajawal', 'Poppins', 'Segoe UI', sans-serif;
      line-height: 1.8;
      color: var(--dark);
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f1f3ff 100%);
      direction: rtl;
      overflow-x: hidden;
      min-height: 100vh;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html {
      scroll-behavior: smooth;
    }

    .container {
      max-width: 1400px;
      margin: 0 auto;
      padding: 0 30px;
    }

    section {
      padding: 80px 0;
      position: relative;
    }

    /* ============== Animations ============== */
    @keyframes fadeIn {
      from { 
        opacity: 0;
        transform: translateY(20px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.8; }
    }

    @keyframes neonGlow {
      0%, 100% { 
        box-shadow: var(--neon-primary);
      }
      50% { 
        box-shadow: 0 0 30px rgba(106, 17, 203, 1);
      }
    }

    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }

    /* ============== Loading & Error ============== */
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: var(--gradient-dark);
      position: relative;
      overflow: hidden;
    }

    .loading-container::before {
      content: '';
      position: absolute;
      width: 300%;
      height: 300%;
      background: 
        radial-gradient(circle, transparent 20%, rgba(106, 17, 203, 0.1) 21%, transparent 22%),
        radial-gradient(circle, transparent 20%, rgba(0, 219, 222, 0.1) 21%, transparent 22%);
      background-size: 150px 150px;
      animation: rotate 20s linear infinite;
    }

    .spinner {
      width: 80px;
      height: 80px;
      border: 4px solid transparent;
      border-radius: 50%;
      border-top: 4px solid var(--secondary);
      border-right: 4px solid var(--primary);
      border-bottom: 4px solid var(--accent);
      animation: rotate 1s linear infinite;
      margin-bottom: 30px;
      position: relative;
      z-index: 2;
      box-shadow: var(--box-shadow-neon);
    }

    .loading-container h2 {
      color: white;
      font-size: 1.8rem;
      font-weight: 700;
      position: relative;
      z-index: 2;
      background: linear-gradient(45deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: pulse 2s ease-in-out infinite;
    }

    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    /* ============== Header ============== */
    .course-header {
      background: var(--gradient-dark);
      color: white;
      padding: 120px 0 60px;
      position: relative;
      overflow: hidden;
    }

    .course-header::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: 
        radial-gradient(circle at 20% 80%, rgba(106, 17, 203, 0.4) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(0, 219, 222, 0.4) 0%, transparent 50%);
      animation: gradient 15s ease infinite;
      background-size: 400% 400%;
      filter: blur(40px);
      opacity: 0.5;
    }

    @keyframes gradient {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    .header-content {
      position: relative;
      z-index: 2;
      animation: fadeIn 1s ease;
    }

    .course-badge {
      display: inline-block;
      background: var(--gradient-accent);
      color: white;
      padding: 8px 20px;
      border-radius: 30px;
      font-size: 0.9rem;
      font-weight: 700;
      margin-bottom: 20px;
      box-shadow: var(--neon-accent);
    }

    .course-title {
      font-size: 3.5rem;
      font-weight: 900;
      margin-bottom: 20px;
      background: linear-gradient(135deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1.2;
    }

    .course-description {
      font-size: 1.3rem;
      color: rgba(255, 255, 255, 0.9);
      line-height: 1.8;
      max-width: 800px;
      margin-bottom: 30px;
    }

    .course-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 30px;
      margin-bottom: 40px;
    }

    .meta-item {
      display: flex;
      align-items: center;
      gap: 10px;
      color: rgba(255, 255, 255, 0.85);
      font-size: 1.1rem;
    }

    .course-instructor {
      display: inline-flex;
      align-items: center;
      gap: 15px;
      color: white;
      text-decoration: none;
      padding: 12px 25px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: var(--border-radius-lg);
      transition: var(--transition-smooth);
      border: 2px solid transparent;
    }

    .course-instructor:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: var(--secondary);
      transform: translateX(-10px);
    }

    /* ============== Buttons ============== */
    .btn {
      padding: 16px 36px;
      border-radius: var(--border-radius-xl);
      font-size: 1.1rem;
      font-weight: 700;
      cursor: pointer;
      transition: var(--transition-smooth);
      border: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      text-decoration: none;
      position: relative;
      overflow: hidden;
    }

    .btn:hover {
      transform: translateY(-5px);
    }

    .btn-primary {
      background: var(--gradient-primary);
      color: white;
      box-shadow: var(--box-shadow-3d), var(--neon-primary);
    }

    .btn-primary:hover {
      box-shadow: var(--box-shadow-hover), 0 0 40px rgba(106, 17, 203, 0.6);
    }

    .btn-secondary {
      background: var(--gradient-secondary);
      color: white;
      box-shadow: var(--box-shadow-3d), 0 10px 30px rgba(0, 219, 222, 0.4);
    }

    .btn-outline {
      background: transparent;
      color: white;
      border: 3px solid white;
      box-shadow: var(--box-shadow-sm);
    }

    .btn-outline:hover {
      background: white;
      color: var(--dark);
    }

    .btn-watch {
      background: var(--success);
      color: white;
      box-shadow: var(--box-shadow-3d), 0 10px 30px rgba(0, 184, 148, 0.4);
    }

    /* ============== Course Info ============== */
    .course-info-section {
      background: white;
      position: relative;
    }

    .info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 30px;
      margin-top: 40px;
    }

    .info-card {
      background: white;
      padding: 30px;
      border-radius: var(--border-radius-lg);
      box-shadow: var(--box-shadow-sm);
      transition: var(--transition-smooth);
      border: 2px solid transparent;
    }

    .info-card:hover {
      transform: translateY(-10px);
      border-color: var(--primary);
      box-shadow: var(--box-shadow-md);
    }

    .info-card h3 {
      font-size: 1.5rem;
      font-weight: 800;
      margin-bottom: 20px;
      color: var(--primary);
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .info-list {
      list-style: none;
    }

    .info-list li {
      padding: 12px 0;
      border-bottom: 1px solid var(--light-gray);
      display: flex;
      align-items: center;
      gap: 15px;
    }

    .info-list li:last-child {
      border-bottom: none;
    }

    /* ============== Course Content ============== */
    .content-section {
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      position: relative;
    }

    .section-header {
      text-align: center;
      margin-bottom: 60px;
      position: relative;
    }

    .section-title {
      font-size: 2.5rem;
      font-weight: 900;
      margin-bottom: 20px;
      background: linear-gradient(135deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      position: relative;
      display: inline-block;
    }

    .section-title::after {
      content: '';
      position: absolute;
      bottom: -15px;
      right: 50%;
      transform: translateX(50%);
      width: 100px;
      height: 5px;
      background: var(--gradient-primary);
      border-radius: 5px;
      box-shadow: var(--neon-primary);
    }

    .section-subtitle {
      font-size: 1.2rem;
      color: var(--gray);
      max-width: 700px;
      margin: 25px auto 0;
      line-height: 1.8;
    }

    .content-accordion {
      max-width: 1000px;
      margin: 0 auto;
    }

    .accordion-item {
      background: white;
      border-radius: var(--border-radius-lg);
      margin-bottom: 20px;
      overflow: hidden;
      box-shadow: var(--box-shadow-sm);
      transition: var(--transition-smooth);
      border: 2px solid transparent;
    }

    .accordion-item.open {
      border-color: var(--primary);
      box-shadow: var(--box-shadow-md);
    }

    .accordion-header {
      padding: 25px 30px;
      display: flex;
      align-items: center;
      gap: 20px;
      cursor: pointer;
      transition: var(--transition-smooth);
      background: white;
    }

    .accordion-item.open .accordion-header {
      background: var(--gradient-primary);
      color: white;
    }

    .section-icon {
      font-size: 1.5rem;
      flex-shrink: 0;
    }

    .section-title-text {
      font-size: 1.4rem;
      font-weight: 800;
      flex: 1;
      margin: 0;
    }

    .video-count {
      background: rgba(255, 255, 255, 0.2);
      color: white;
      padding: 6px 15px;
      border-radius: 20px;
      font-size: 0.9rem;
      font-weight: 700;
    }

    .accordion-content {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.6s ease;
    }

    .accordion-item.open .accordion-content {
      max-height: 2000px;
    }

    .video-list {
      padding: 0 30px 30px;
    }

    .video-item {
      padding: 20px;
      border-radius: var(--border-radius-md);
      background: rgba(106, 17, 203, 0.05);
      margin-bottom: 15px;
      transition: var(--transition-smooth);
      border: 2px solid transparent;
    }

    .video-item:hover {
      background: rgba(106, 17, 203, 0.1);
      border-color: rgba(106, 17, 203, 0.2);
      transform: translateX(-10px);
    }

    .video-item.locked {
      background: rgba(220, 220, 220, 0.1);
      position: relative;
      cursor: not-allowed;
    }

    .video-header {
      display: flex;
      align-items: center;
      gap: 15px;
      margin-bottom: 15px;
    }

    .video-icon {
      width: 40px;
      height: 40px;
      background: var(--gradient-primary);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      flex-shrink: 0;
    }

    .video-title {
      font-size: 1.2rem;
      font-weight: 700;
      flex: 1;
      color: var(--dark);
    }

    .video-duration {
      color: var(--gray);
      font-size: 0.9rem;
      font-weight: 600;
    }

    .video-preview {
      margin-top: 15px;
      border-radius: var(--border-radius-md);
      overflow: hidden;
      position: relative;
    }

    .lock-overlay {
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.2rem;
      gap: 15px;
    }

    /* ============== FAQ ============== */
    .faq-section {
      background: white;
      position: relative;
    }

    .faq-list {
      max-width: 900px;
      margin: 0 auto;
    }

    .faq-item {
      background: white;
      border-radius: var(--border-radius-lg);
      margin-bottom: 20px;
      overflow: hidden;
      box-shadow: var(--box-shadow-sm);
      transition: var(--transition-smooth);
      border: 2px solid transparent;
    }

    .faq-item.open {
      border-color: var(--primary);
      box-shadow: var(--box-shadow-md);
    }

    .faq-question {
      padding: 25px 30px;
      display: flex;
      align-items: center;
      gap: 20px;
      cursor: pointer;
      transition: var(--transition-smooth);
      background: white;
    }

    .faq-item.open .faq-question {
      background: var(--gradient-primary);
      color: white;
    }

    .question-icon {
      font-size: 1.5rem;
      flex-shrink: 0;
    }

    .question-text {
      font-size: 1.3rem;
      font-weight: 800;
      flex: 1;
      margin: 0;
    }

    .faq-answer {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.6s ease;
    }

    .faq-item.open .faq-answer {
      max-height: 500px;
    }

    .answer-content {
      padding: 0 30px 30px;
      color: var(--gray);
      line-height: 1.8;
      font-size: 1.1rem;
    }

    /* ============== Reviews ============== */
    .reviews-section {
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      position: relative;
    }

    .reviews-stats {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 50px;
      margin-bottom: 50px;
      flex-wrap: wrap;
    }

    .rating-overall {
      text-align: center;
    }

    .rating-number {
      font-size: 4rem;
      font-weight: 900;
      background: linear-gradient(135deg, var(--primary), var(--accent));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1;
    }

    .rating-stars {
      display: flex;
      gap: 5px;
      margin: 15px 0;
      justify-content: center;
    }

    .star {
      font-size: 1.5rem;
      color: var(--warning);
    }

    .reviews-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
      gap: 30px;
      margin-bottom: 50px;
    }

    .review-card {
      background: white;
      padding: 30px;
      border-radius: var(--border-radius-lg);
      box-shadow: var(--box-shadow-sm);
      transition: var(--transition-smooth);
      border: 2px solid transparent;
    }

    .review-card:hover {
      transform: translateY(-10px);
      border-color: var(--primary);
      box-shadow: var(--box-shadow-md);
    }

    .review-header {
      display: flex;
      align-items: center;
      gap: 15px;
      margin-bottom: 20px;
    }

    .reviewer-avatar {
      width: 60px;
      height: 60px;
      background: var(--gradient-primary);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.5rem;
      font-weight: 700;
      flex-shrink: 0;
    }

    .reviewer-info {
      flex: 1;
    }

    .reviewer-name {
      font-size: 1.2rem;
      font-weight: 800;
      margin-bottom: 5px;
      color: var(--dark);
    }

    .review-content {
      color: var(--gray);
      line-height: 1.8;
      font-style: italic;
      margin-bottom: 20px;
    }

    .review-form {
      background: white;
      padding: 40px;
      border-radius: var(--border-radius-lg);
      box-shadow: var(--box-shadow-md);
      max-width: 600px;
      margin: 0 auto;
    }

    .form-group {
      margin-bottom: 25px;
    }

    .form-label {
      display: block;
      margin-bottom: 10px;
      font-weight: 700;
      color: var(--dark);
      font-size: 1.1rem;
    }

    .form-input {
      width: 100%;
      padding: 15px 20px;
      border: 2px solid var(--light-gray);
      border-radius: var(--border-radius-md);
      font-size: 1.1rem;
      transition: var(--transition-smooth);
      background: white;
    }

    .form-input:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 4px rgba(106, 17, 203, 0.1);
    }

    .form-textarea {
      min-height: 150px;
      resize: vertical;
    }

    .rating-input {
      display: flex;
      gap: 10px;
      direction: ltr;
    }

    .rating-star {
      font-size: 2rem;
      cursor: pointer;
      color: var(--light-gray);
      transition: var(--transition-smooth);
    }

    .rating-star.active {
      color: var(--warning);
    }

    /* ============== Modal ============== */
    .modal-overlay {
      position: fixed;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.3s ease;
    }

    .modal {
      background: white;
      padding: 40px;
      border-radius: var(--border-radius-xl);
      width: 90%;
      max-width: 500px;
      box-shadow: var(--box-shadow-lg);
      animation: fadeIn 0.3s ease 0.1s both;
    }

    .modal-header {
      margin-bottom: 25px;
    }

    .modal-title {
      font-size: 1.8rem;
      font-weight: 900;
      color: var(--dark);
      text-align: center;
    }

    .modal-actions {
      display: flex;
      gap: 15px;
      margin-top: 30px;
    }

    .modal-actions .btn {
      flex: 1;
    }

    /* ============== Footer ============== */
    .course-footer {
      background: var(--dark);
      color: white;
      padding: 60px 0 30px;
      text-align: center;
    }

    .back-home {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      color: white;
      text-decoration: none;
      font-size: 1.1rem;
      font-weight: 600;
      padding: 15px 30px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: var(--border-radius-lg);
      transition: var(--transition-smooth);
      margin-bottom: 40px;
    }

    .back-home:hover {
      background: var(--gradient-primary);
      transform: translateY(-5px);
    }

    .copyright {
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.95rem;
      padding-top: 30px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    /* ============== Responsive ============== */
    @media (max-width: 992px) {
      .course-title { font-size: 2.5rem; }
      .section-title { font-size: 2rem; }
      .course-meta { flex-direction: column; gap: 15px; }
      .reviews-grid { grid-template-columns: 1fr; }
      .info-grid { grid-template-columns: 1fr; }
    }

    @media (max-width: 768px) {
      .container { padding: 0 20px; }
      .course-title { font-size: 2rem; }
      .course-description { font-size: 1.1rem; }
      .section-title { font-size: 1.8rem; }
      .btn { padding: 14px 28px; font-size: 1rem; }
      .modal { padding: 30px 20px; }
    }

    /* ============== Utilities ============== */
    .text-center { text-align: center; }
    .mt-20 { margin-top: 20px; }
    .mt-30 { margin-top: 30px; }
    .mt-40 { margin-top: 40px; }
    .mb-20 { margin-bottom: 20px; }
    .mb-30 { margin-bottom: 30px; }
    .mb-40 { margin-bottom: 40px; }
    .glass { background: var(--glass-bg); backdrop-filter: blur(10px); border: 1px solid var(--glass-border); }
    .shadow-lg { box-shadow: var(--box-shadow-lg); }
    .shadow-md { box-shadow: var(--box-shadow-md); }
    .shadow-sm { box-shadow: var(--box-shadow-sm); }
    .rounded-lg { border-radius: var(--border-radius-lg); }
    .rounded-xl { border-radius: var(--border-radius-xl); }
    .gradient-text { background: var(--gradient-primary); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
  `;

// ============== COMPONENT ==============
export default function CoursePage() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [trainer, setTrainer] = useState(null);
  const [academy, setAcademy] = useState(null);
  const [sections, setSections] = useState([]);
  const [videos, setVideos] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [isEnrolled, setIsEnrolled] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);

  const [showActivate, setShowActivate] = useState(false);
  const [activationCode, setActivationCode] = useState("");
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationError, setActivationError] = useState("");
  const [activationSuccess, setActivationSuccess] = useState("");

  // Accordion states
  const [openSection, setOpenSection] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchCoursePage(courseId);

        setCourse(data.course);
        setTrainer(data.trainer);
        setAcademy(data.academy);
        setSections(data.sections || []);
        setVideos(data.videos || []);
        setRequirements(data.requirements || []);
        setOutcomes(data.outcomes || []);
        setFaqs(data.faqs || []);

        // Check enrollment
        try {
          const enrolled = await checkCourseEnrollment(courseId);
          setIsLoggedIn(true);
          setIsEnrolled(enrolled);
        } catch (e) {
          if (e.response?.status === 401) {
            setIsLoggedIn(false);
            setIsEnrolled(false);
          }
        }

        // Fetch reviews
        const courseReviews = await fetchCourseReviews(courseId);
        setReviews(courseReviews || []);

      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [courseId]);

  const handleActivate = async () => {
    if (!activationCode) {
      setActivationError("الرجاء إدخال كود التفعيل");
      return;
    }

    try {
      setActivationLoading(true);
      setActivationError("");

      await activateCourse(courseId, activationCode);

      setActivationSuccess("تم تفعيل الكورس بنجاح 🎉");
      setActivationCode("");
      setIsEnrolled(true);
      setShowActivate(false);

    } catch (e) {
      setActivationError(
        e?.response?.data?.message || "كود التفعيل غير صحيح"
      );
    } finally {
      setActivationLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    try {
      setReviewLoading(true);

      await submitCourseReview(courseId, rating, comment);

      setComment("");
      setRating(5);

      const updated = await fetchCourseReviews(courseId);
      setReviews(updated);

    } catch (e) {
      alert(e?.response?.data?.message || "حدث خطأ");
    } finally {
      setReviewLoading(false);
    }
  };

  // Calculate average rating
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 0;

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <style>{styles}</style>
        <div className="spinner"></div>
        <h2>جارٍ تحميل الكورس...</h2>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="error-container">
        <style>{styles}</style>
        <h2>الكورس غير موجود</h2>
        <button 
          className="btn btn-primary"
          onClick={() => navigate(-1)}
          style={{ marginTop: '30px' }}
        >
          <Icons.ArrowLeft />
          <span>العودة للخلف</span>
        </button>
      </div>
    );
  }

  return (
    <div className="course-page">
      <style>{styles}</style>

      {/* ================= COURSE HEADER ================= */}
      <section className="course-header">
        <div className="container">
          <div className="header-content">
            <div className="course-badge">{course.level || "جميع المستويات"}</div>
            
            <h1 className="course-title">{course.title}</h1>
            
            <p className="course-description">{course.description}</p>

            <div className="course-meta">
              <div className="meta-item">
                <Icons.Clock />
                <span>المدة: {course.duration || "10 ساعات"}</span>
              </div>
              <div className="meta-item">
                <Icons.Users />
                <span>الطلاب: {course.students_count || "100+"}</span>
              </div>
              <div className="meta-item">
                <Icons.Trophy />
                <span>المستوى: {course.level || "متوسط"}</span>
              </div>
              <div className="meta-item">
                <Icons.Book />
                <span>اللغة: {course.language || "العربية"}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ 
                background: 'rgba(255,255,255,0.1)',
                padding: '12px 25px',
                borderRadius: 'var(--border-radius-lg)',
                border: '2px solid rgba(255,255,255,0.2)'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '900' }}>{course.price || "49"}$</div>
                <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>سعر الكورس</div>
              </div>

              {trainer && (
                <a 
                  href={`/trainers/${trainer.id}`}
                  className="course-instructor"
                >
                  <Icons.Graduation />
                  <div>
                    <div style={{ fontWeight: '700' }}>المدرّب</div>
                    <div>{trainer.name}</div>
                  </div>
                </a>
              )}

              {academy && (
                <a 
                  href={`/academies/${academy.id}`}
                  className="course-instructor"
                >
                  <Icons.Graduation />
                  <div>
                    <div style={{ fontWeight: '700' }}>الأكاديمية</div>
                    <div>{academy.name}</div>
                  </div>
                </a>
              )}
            </div>

            {/* Action Button */}
            <div style={{ marginTop: '40px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {isEnrolled ? (
                <button
                  className="btn btn-watch"
                  onClick={() => navigate(`/student/courses/${courseId}/watch`)}
                >
                  <Icons.Play />
                  <span>مشاهدة الكورس</span>
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowActivate(true)}
                >
                  <Icons.Play />
                  <span>الانضمام إلى الكورس</span>
                </button>
              )}

              <button className="btn btn-outline">
                <Icons.Share />
                <span>مشاركة الكورس</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ================= COURSE INFO ================= */}
      <section className="course-info-section">
        <div className="container">
          <div className="info-grid">
            {/* Requirements */}
            <div className="info-card">
              <h3><Icons.Check /> متطلبات الكورس</h3>
              {requirements.length > 0 ? (
                <ul className="info-list">
                  {requirements.map((req, idx) => (
                    <li key={idx}>
                      <Icons.Check style={{ color: 'var(--success)' }} />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ color: 'var(--gray)', fontStyle: 'italic' }}>
                  لا توجد متطلبات خاصة
                </p>
              )}
            </div>

            {/* Outcomes */}
            <div className="info-card">
              <h3><Icons.Trophy /> ماذا ستتعلم</h3>
              {outcomes.length > 0 ? (
                <ul className="info-list">
                  {outcomes.map((out, idx) => (
                    <li key={idx}>
                      <Icons.Check style={{ color: 'var(--success)' }} />
                      <span>{out}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ color: 'var(--gray)', fontStyle: 'italic' }}>
                  سيتم إضافة التفاصيل قريباً
                </p>
              )}
            </div>

            {/* Course Stats */}
            <div className="info-card">
              <h3><Icons.Book /> إحصائيات الكورس</h3>
              <ul className="info-list">
                <li>
                  <Icons.Play style={{ color: 'var(--primary)' }} />
                  <span>عدد الدروس: {videos.length}</span>
                </li>
                <li>
                  <Icons.Clock style={{ color: 'var(--warning)' }} />
                  <span>المدة الإجمالية: {course.duration || "10 ساعات"}</span>
                </li>
                <li>
                  <Icons.Users style={{ color: 'var(--info)' }} />
                  <span>عدد الطلاب: {course.students_count || "100+"}</span>
                </li>
                <li>
                  <Icons.Star style={{ color: 'var(--warning)' }} />
                  <span>التقييم: {averageRating}/5 ({reviews.length} تقييم)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ================= COURSE CONTENT ================= */}
      <section className="content-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">محتوى الكورس</h2>
            <p className="section-subtitle">
              استكشف جميع دروس ومواد الكورس
            </p>
          </div>

          <div className="content-accordion">
            {sections.length > 0 ? sections.map((section) => (
              <div 
                key={section.id} 
                className={`accordion-item ${openSection === section.id ? 'open' : ''}`}
              >
                <div 
                  className="accordion-header"
                  onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
                >
                  <div className="section-icon">
                    <Icons.Book />
                  </div>
                  <h3 className="section-title-text">{section.title}</h3>
                  <div className="video-count">
                    {videos.filter(v => v.section_id === section.id).length} درس
                  </div>
                  <div className="section-icon">
                    {openSection === section.id ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
                  </div>
                </div>

                <div className="accordion-content">
                  <div className="video-list">
                    {videos
                      .filter(video => video.section_id === section.id)
                      .map((video) => (
                        <div 
                          key={video.id} 
                          className={`video-item ${!isEnrolled ? 'locked' : ''}`}
                          onClick={() => {
                            if (isEnrolled && video.video_url) {
                              navigate(`/student/courses/${courseId}/watch?video=${video.id}`);
                            }
                          }}
                        >
                          <div className="video-header">
                            <div className="video-icon">
                              {isEnrolled ? <Icons.Play /> : <Icons.Lock />}
                            </div>
                            <div className="video-title">{video.title}</div>
                            <div className="video-duration">{video.duration || "10:00"}</div>
                          </div>
                          
                          {video.description && (
                            <p style={{ color: 'var(--gray)', fontSize: '0.95rem' }}>
                              {video.description}
                            </p>
                          )}

                          {isEnrolled ? (
                            video.video_url && (
                              <div className="video-preview">
                                <video
                                  src={video.video_url}
                                  controls
                                  style={{ width: '100%', borderRadius: 'var(--border-radius-md)' }}
                                />
                              </div>
                            )
                          ) : (
                            <div className="video-preview" style={{ position: 'relative' }}>
                              {video.thumbnail && (
                                <img 
                                  src={video.thumbnail} 
                                  alt={video.title}
                                  style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                                />
                              )}
                              <div className="lock-overlay">
                                <Icons.Lock style={{ fontSize: '2rem' }} />
                                <div>هذا الفيديو متاح فقط للمشتركين</div>
                                <button
                                  className="btn btn-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowActivate(true);
                                  }}
                                  style={{ marginTop: '10px' }}
                                >
                                  <span>اشترك الآن</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )) : (
              <div style={{ textAlign: 'center', padding: '50px', background: 'white', borderRadius: 'var(--border-radius-lg)' }}>
                <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📚</div>
                <h3 style={{ color: 'var(--gray)' }}>لا يوجد محتوى بعد</h3>
                <p style={{ color: 'var(--gray)' }}>سيتم إضافة المحتوى قريباً</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      {faqs.length > 0 && (
        <section className="faq-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">الأسئلة الشائعة</h2>
              <p className="section-subtitle">
                أجوبة على الأسئلة الأكثر شيوعاً حول هذا الكورس
              </p>
            </div>

            <div className="faq-list">
              {faqs.map((faq, idx) => (
                <div 
                  key={idx} 
                  className={`faq-item ${openFaq === idx ? 'open' : ''}`}
                >
                  <div 
                    className="faq-question"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  >
                    <div className="question-icon">
                      <Icons.Message />
                    </div>
                    <h3 className="question-text">{faq.question}</h3>
                    <div className="question-icon">
                      {openFaq === idx ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
                    </div>
                  </div>

                  <div className="faq-answer">
                    <div className="answer-content">
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================= REVIEWS ================= */}
      <section className="reviews-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">تقييمات الطلاب</h2>
            <p className="section-subtitle">
              آراء الطلاب الذين درسوا هذا الكورس
            </p>
          </div>

          <div className="reviews-stats">
            <div className="rating-overall">
              <div className="rating-number">{averageRating}</div>
              <div className="rating-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} className="star">
                    <Icons.Star />
                  </span>
                ))}
              </div>
              <div style={{ color: 'var(--gray)' }}>
                بناءً على {reviews.length} تقييم
              </div>
            </div>
          </div>

          {reviews.length > 0 ? (
            <div className="reviews-grid">
              {reviews.slice(0, 6).map((review) => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <div className="reviewer-avatar">
                      {review.student?.charAt(0)?.toUpperCase() || "ط"}
                    </div>
                    <div className="reviewer-info">
                      <div className="reviewer-name">{review.student || "طالب"}</div>
                      <div className="rating-stars" style={{ justifyContent: 'flex-start' }}>
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className="star" style={{ 
                            color: i < review.rating ? 'var(--warning)' : 'var(--light-gray)',
                            fontSize: '1.2rem'
                          }}>
                            <Icons.Star />
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="review-content">
                    {review.comment || "تقييم جيد للكورس"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <div style={{ 
                width: '120px', 
                height: '120px', 
                background: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 30px',
                fontSize: '3rem',
                boxShadow: 'var(--box-shadow-sm)'
              }}>
                <Icons.Star />
              </div>
              <h3 style={{ 
                color: 'var(--gray)', 
                fontSize: '1.5rem',
                marginBottom: '20px'
              }}>
                لا يوجد تقييمات بعد
              </h3>
              <p style={{ color: 'var(--gray)', maxWidth: '500px', margin: '0 auto' }}>
                كن أول من يقيّم هذا الكورس
              </p>
            </div>
          )}

          {/* Add Review Form */}
          {isLoggedIn && isEnrolled && (
            <div className="review-form">
              <h3 style={{ textAlign: 'center', marginBottom: '30px' }}>أضف تقييمك</h3>
              
              <div className="form-group">
                <label className="form-label">التقييم</label>
                <div className="rating-input">
                  {[5, 4, 3, 2, 1].map((star) => (
                    <span
                      key={star}
                      className={`rating-star ${rating >= star ? 'active' : ''}`}
                      onClick={() => setRating(star)}
                    >
                      <Icons.Star />
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">التعليق (اختياري)</label>
                <textarea
                  className="form-input form-textarea"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="شاركنا رأيك في الكورس..."
                />
              </div>

              <button
                className="btn btn-primary"
                onClick={handleSubmitReview}
                disabled={reviewLoading}
                style={{ width: '100%' }}
              >
                {reviewLoading ? "جارٍ الإرسال..." : "إرسال التقييم"}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ================= ACTIVATE MODAL ================= */}
      {showActivate && (
        <div className="modal-overlay" onClick={() => setShowActivate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">تفعيل الكورس</h3>
            </div>

            <div className="form-group">
              <label className="form-label">أدخل كود التفعيل</label>
              <input
                type="text"
                className="form-input"
                placeholder="مثال: ABC123"
                value={activationCode}
                onChange={(e) => {
                  setActivationCode(e.target.value);
                  setActivationError("");
                  setActivationSuccess("");
                }}
              />
            </div>

            {activationError && (
              <div style={{ 
                padding: '15px', 
                background: 'rgba(255, 107, 107, 0.1)', 
                border: '2px solid var(--danger)',
                borderRadius: 'var(--border-radius-md)',
                color: 'var(--danger)',
                marginTop: '20px'
              }}>
                {activationError}
              </div>
            )}

            {activationSuccess && (
              <div style={{ 
                padding: '15px', 
                background: 'rgba(0, 184, 148, 0.1)', 
                border: '2px solid var(--success)',
                borderRadius: 'var(--border-radius-md)',
                color: 'var(--success)',
                marginTop: '20px'
              }}>
                {activationSuccess}
              </div>
            )}

            <div className="modal-actions">
              <button
                className="btn"
                onClick={() => setShowActivate(false)}
                style={{ background: 'var(--light-gray)' }}
              >
                إلغاء
              </button>
              <button
                className="btn btn-primary"
                onClick={handleActivate}
                disabled={activationLoading}
              >
                {activationLoading ? "جارٍ التفعيل..." : "تفعيل الكورس"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= FOOTER ================= */}
      <footer className="course-footer">
        <div className="container">
          <a href="/" className="back-home">
            <Icons.Home />
            <span>العودة للرئيسية</span>
          </a>
          
          <div className="copyright">
            <p>© {new Date().getFullYear()} كورس {course.title}. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}